import { useGetProductTemplateData } from "api/api-hooks/product-templates-hooks";
import { ITableDataEntry } from "components/dashboard/product-template-table/utils/interfaces";
import { Dropdown } from "primereact/dropdown";
import React, { useEffect, useState } from "react";
import { T } from "services/lang/LanguageService";

interface IProductTemplateEntriesDropdownProps {
  safeName: string;
  dateFrom: Date;
  startAttribute: string;
  endAttribute: string;
  setProductItemsDateRange?: (value: string[]) => void;
}

const ProductTemplateEntriesDropdown = (props: IProductTemplateEntriesDropdownProps) => {
  const [allSelections, setAllSelections] = useState<{ label: string; value: string }[]>([]);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  const {
    data: templateData,
    isError: templateError,
    isSuccess: templateSuccess,
    isLoading: isLoadingTemplate,
  } = useGetProductTemplateData(
    {
      safeName: props.safeName,
      dateFrom: new Date(props.dateFrom).toISOString(),
    },
    {
      enabled: !!props.safeName && !!props.dateFrom,
    }
  );

  useEffect(() => {
    if (templateSuccess && templateData) {
      const { startAttribute, endAttribute } = props;
      const selections = templateData.reduce(
        (acc: { label: string; value: string }[], entry: ITableDataEntry) => {
          if (entry.entries[startAttribute].value && entry.entries[endAttribute].value) {
            acc.push({
              label: entry.stringRepresentation ?? "",
              value: entry.id ?? "",
            });
          }
          return acc;
        },
        []
      );
      setAllSelections(selections);
    }
  }, [templateSuccess, templateData]);

  let placeholderText;
  if (templateError) {
    placeholderText = T("error.product-template-selection-error");
  } else if (isLoadingTemplate && !templateError) {
    placeholderText = T("message.product-templates-loading");
  } else {
    placeholderText = T("message.select-product-template");
  }

  const onChange = (e: { value: string }) => {
    const { startAttribute, endAttribute } = props;
    const part = templateData?.find((data: ITableDataEntry) => data.id === e.value);

    if (!part) {
      return;
    }

    let endDate = "";

    const startDate = part.entries[startAttribute]?.value as string;
    if (endAttribute) {
      endDate = part.entries[endAttribute]?.value as string;
    } else {
      endDate = new Date().toISOString();
    }
    if (props.setProductItemsDateRange) {
      props.setProductItemsDateRange([startDate, endDate]);
    }

    setSelectedItem(e.value);
  };

  const selectedTemplate = (option: { label: string; value: string }) => {
    if (option && option.value) {
      return <span>{option.label}</span>;
    }

    return <span>{placeholderText}</span>;
  };

  const itemTemplate = (option: { label: string; value: string }) => {
    return <span>{option.label}</span>;
  };

  return (
    <>
      {templateError && <span>{T("error.product-template-selection-error")}</span>}

      {isLoadingTemplate && !templateError && <span>{T("message.product-templates-loading")}</span>}

      {templateSuccess && (
        <Dropdown
          placeholder={placeholderText}
          value={selectedItem}
          options={allSelections}
          className="h-max"
          filter
          filterMatchMode="contains"
          onChange={e => onChange(e)}
          valueTemplate={selectedTemplate}
          itemTemplate={itemTemplate}
        />
      )}
    </>
  );
};

export default ProductTemplateEntriesDropdown;
